if (!customElements.get('sticky-atc-panel')) {
  class StickyAtcPanel extends HTMLElement {
    constructor() {
      super();

      this.productSection = this.closest('.cc-main-product');
      this.productInfo = this.productSection.querySelector('.product-info');
      this.productForm = this.productSection.querySelector('product-form');

      if ('IntersectionObserver' in window && 'MutationObserver' in window) {
        this.bindEvents();
      }
    }

    // eslint-disable-next-line class-methods-use-this
    disconnectedCallback() {
      window.removeEventListener('scroll', StickyAtcPanel.handleScroll);
    }

    bindEvents() {
      // Watch the ADD-TO-CART FORM on every viewport: keep the sticky panel
      // hidden (--out) until the form has scrolled above the top of the
      // viewport (boundingClientRect.bottom <= 0), then show it. This
      // replaced a desktop branch that watched .product-info instead - that
      // element is now display:contents in the desktop grid layout (no box),
      // so it never "intersected" and the panel showed on load. The form
      // always has a box, so this works on desktop and mobile alike.
      // Owner 2026-08-02: show the sticky bar whenever the real Add-to-Cart region is OFF-screen —
      // whether it's still below the fold (haven't scrolled down to it yet) OR already scrolled above
      // the viewport — and hide it only while that region is actually in view. Driven off the ATC
      // region's own intersection (isIntersecting), not just "has it scrolled past the top". Observing
      // the .product-info__add-to-cart box (qty + button) rather than the whole tall <product-form> so
      // the bar hides precisely when the real button is on screen.
      this.atcRegion = this.productSection.querySelector('.product-info__add-to-cart') || this.productForm;
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.target === this.atcRegion) {
            const panelHidden = entry.isIntersecting;
            this.classList.toggle('sticky-atc-panel--out', panelHidden);
            StickyAtcPanel.toggleLauncher(panelHidden);
          }
        });
      });

      if (this.atcRegion) observer.observe(this.atcRegion);

      // Owner 2026-08-02: the sticky bar is now a single "Add to cart" button (Chewy-style). Forward
      // its click to the REAL add-to-cart submit button inside the buy box so it inherits the selected
      // variant / quantity / Appstle selling-plan and the cart-drawer "Added to cart" behaviour. No new
      // form logic here — just re-trigger the one that already works.
      const stickyTrigger = this.querySelector('[data-sticky-atc-trigger]');
      if (stickyTrigger && this.productForm) {
        stickyTrigger.addEventListener('click', () => {
          const realBtn = this.productForm.querySelector('button[name="add"]');
          if (!realBtn || realBtn.disabled) return;

          /* Owner 2026-08-14: the spinner appeared to be "missing on mobile". It wasn't - the
             loading state lands on the REAL buy-box button, which is scrolled off-screen when
             the sticky bar is showing, so the button the shopper actually taps never changed.
             Show the busy state HERE too, mirroring the real button: add it immediately (so
             there's feedback on the very first frame, not ~350ms later when the submit handler
             runs) and clear it when the real button finishes. */
          stickyTrigger.classList.add('is-loading');
          stickyTrigger.setAttribute('aria-disabled', 'true');

          const done = new MutationObserver(() => {
            if (!realBtn.classList.contains('is-loading')) {
              stickyTrigger.classList.remove('is-loading');
              stickyTrigger.removeAttribute('aria-disabled');
              done.disconnect();
            }
          });
          done.observe(realBtn, { attributes: true, attributeFilter: ['class'] });

          /* Safety net: if the real button never clears (network error, handler bailed), don't
             leave the sticky bar spinning forever. */
          setTimeout(() => {
            stickyTrigger.classList.remove('is-loading');
            stickyTrigger.removeAttribute('aria-disabled');
            done.disconnect();
          }, 8000);

          realBtn.click();
        });
      }

      window.addEventListener('scroll', StickyAtcPanel.handleScroll);
    }

    /**
     * Show/hide the Redeemly store-credit floating launcher in step with the
     * sticky ATC bar. That launcher (#mcb-launcher-widget-root) is a shadow-DOM
     * widget whose button uses z-index:2147483647 (the max, so it can't be out-
     * stacked) AND which sets `display:block !important` INLINE on its root - so
     * NO stylesheet, even !important, can hide it, and the sticky bar can't be
     * painted over it. The one thing that beats an inline !important is another
     * inline !important set via JS, which is what we do here: hide the launcher
     * while the bar is showing (it otherwise sits on top of the bar) and restore
     * it when the bar goes away. Queried fresh each call because Redeemly loads
     * async and may not exist on the observer's first fire.
     * @param {boolean} panelHidden - true when the sticky ATC bar is hidden.
     */
    static toggleLauncher(panelHidden) {
      const launcher = document.getElementById('mcb-launcher-widget-root');
      if (!launcher) return;
      launcher.style.setProperty('display', panelHidden ? 'block' : 'none', 'important');
    }

    /**
     * Watches for a scroll to the bottom of the page
     */
    static handleScroll() {
      document.body.classList.toggle(
        'scrolled-to-bottom',
        window.scrollY + window.innerHeight + 100 > document.body.scrollHeight
      );
    }
  }

  customElements.define('sticky-atc-panel', StickyAtcPanel);
}
